import { useEffect, useRef, useState } from "react"
import {
  previewCodeCreate, createCodes, getCodeCreate, codeCreateDownloadUrl, fetchMeCodes,
} from "../../lib/api"
import { cleanMessage } from "./GroupsDialog"

// Create many codes from a list
// (docs/plans/2026-09-23-spreadsheet-creation.plan.md).
//
//   compose  → paste links or upload a CSV; optionally a folder and a code to
//              copy the look of
//   review   → the server's check of every row, by line; nothing written yet
//   running  → the batch is a job, so this polls until it's done
//   done     → what was made, and the CSV + ZIP of every new code
//
// Row caps and the daily budget are abuse guards: their errors come from the
// server and name a contact path. There is no upgrade prompt here — a new code
// has zero scans, so nothing about it is locked.
const REASONS = {
  invalid_destination: "not a valid link",
  name_too_long:       "name longer than 80 characters",
  group_name_too_long: "folder name longer than 80 characters",
  destination_unsafe:  "link flagged as unsafe",
  invalid:             "couldn't be saved",
  failed:              "couldn't be saved — try again",
}

const POLL_MS = 1000
const noun = (n) => `${n.toLocaleString()} ${n === 1 ? "code" : "codes"}`

export default function CreateManyDialog({ groups, onClose, onDone }) {
  const dialogRef = useRef(null)
  const [mode, setMode] = useState("paste") // "paste" | "csv"
  const [text, setText] = useState("")
  const [file, setFile] = useState(null)
  const [groupId, setGroupId] = useState("")
  const [referenceToken, setReferenceToken] = useState("")
  const [step, setStep] = useState("compose") // "compose" | "review" | "running" | "done"
  const [preview, setPreview] = useState(null)
  const [batch, setBatch] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const wroteRef = useRef(false)
  function close() {
    if (wroteRef.current) onDone()
    onClose()
  }
  const closeRef = useRef(close)
  closeRef.current = close

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    function handleCancel(e) { e.preventDefault(); closeRef.current() }
    function handleClick(e) { if (e.target === dialog) closeRef.current() }
    dialog?.addEventListener("cancel", handleCancel)
    dialog?.addEventListener("click", handleClick)
    return () => {
      dialog?.removeEventListener("cancel", handleCancel)
      dialog?.removeEventListener("click", handleClick)
    }
  }, [])

  // Poll while the job runs. A failed poll is retried on the next tick rather
  // than ending the wait: the batch is still running on the server.
  useEffect(() => {
    if (step !== "running" || !batch) return
    let cancelled = false
    const timer = setInterval(async () => {
      try {
        const next = await getCodeCreate(batch.id)
        if (cancelled) return
        setBatch(next)
        if (next.status === "done" || next.status === "failed") {
          // wroteRef stays set from confirm: new folders exist even when every
          // row was skipped, so the list still needs refreshing.
          setStep("done")
        }
      } catch { /* try again next tick */ }
    }, POLL_MS)
    return () => { cancelled = true; clearInterval(timer) }
  }, [step, batch?.id])

  function request() {
    return {
      ...(mode === "csv" ? { file } : { text }),
      groupId: groupId || null,
      referenceToken: referenceToken || null,
    }
  }

  async function run(action) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(cleanMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const review = () => run(async () => {
    setPreview(await previewCodeCreate(request()))
    setStep("review")
  })

  const confirm = () => run(async () => {
    setBatch(await createCodes(request()))
    wroteRef.current = true // new folders may exist even before a code does
    setStep("running")
  })

  const canReview = !busy && (mode === "csv" ? !!file : text.trim().length > 0)

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="create-many-title"
      className="p-0 m-auto bg-transparent backdrop:bg-coal/55 backdrop:backdrop-blur-sm"
    >
      <div className="w-[min(92vw,520px)] max-h-[85dvh] flex flex-col bg-white dark:bg-midnight text-ink dark:text-cloud rounded-xl shadow-lg overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-[color:var(--color-border)] dark:border-storm">
          <div>
            <h2 id="create-many-title" className="font-display font-bold text-lg">Create many</h2>
            <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-0.5">
              One code per link. Every one can be repointed later.
            </p>
          </div>
          <button type="button" onClick={close} aria-label="Close"
            className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud p-1 bg-transparent border-0 cursor-pointer">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {step === "compose" && (
            <Compose mode={mode} setMode={(m) => { setMode(m); setError(null) }}
              text={text} setText={setText} file={file} setFile={setFile}
              groups={groups} groupId={groupId} setGroupId={setGroupId}
              referenceToken={referenceToken} setReferenceToken={setReferenceToken} />
          )}
          {step === "review" && preview && <Review preview={preview} />}
          {step === "running" && batch && (
            <p role="status" className="text-base">
              Creating <span className="font-mono font-semibold">{noun(batch.requested)}</span>…
              <span className="block text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-1">
                You can close this. They'll appear in your list when they're ready.
              </span>
            </p>
          )}
          {step === "done" && batch && <Done batch={batch} />}
          {error && <p role="alert" className="mt-3 text-xs text-fail">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[color:var(--color-border)] dark:border-storm">
          {step === "compose" && (
            <>
              <GhostButton onClick={close}>Cancel</GhostButton>
              <PrimaryButton onClick={review} disabled={!canReview}>{busy ? "Checking…" : "Review"}</PrimaryButton>
            </>
          )}
          {step === "review" && (
            <>
              <GhostButton onClick={() => { setStep("compose"); setError(null) }} disabled={busy}>Back</GhostButton>
              <PrimaryButton onClick={confirm} disabled={busy || preview.creatable === 0}>
                {busy ? "Starting…" : preview.creatable === 0 ? "Nothing to create" : `Create ${noun(preview.creatable)}`}
              </PrimaryButton>
            </>
          )}
          {(step === "running" || step === "done") && <PrimaryButton onClick={close}>Done</PrimaryButton>}
        </div>
      </div>
    </dialog>
  )
}

function Compose({ mode, setMode, text, setText, file, setFile, groups, groupId, setGroupId, referenceToken, setReferenceToken }) {
  const inputClass = "w-full border border-[color:var(--color-border)] dark:border-storm rounded px-3 py-2 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-2 dark:focus:ring-offset-midnight focus:border-voltage"
  return (
    <div>
      <div role="tablist" aria-label="How to add links" className="inline-flex rounded border border-[color:var(--color-border)] dark:border-storm p-0.5 mb-4">
        {[ [ "paste", "Paste links" ], [ "csv", "Upload a CSV" ] ].map(([ value, label ]) => (
          <button key={value} type="button" role="tab" aria-selected={mode === value} onClick={() => setMode(value)}
            className={`px-3 py-1.5 text-sm rounded-[10px] transition ${mode === value
              ? "bg-coal text-cloud dark:bg-cloud dark:text-coal font-semibold"
              : "text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud"}`}>
            {label}
          </button>
        ))}
      </div>

      {mode === "paste" ? (
        <label className="block">
          <span className="block text-sm font-semibold mb-1.5">Links, one per line</span>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} autoFocus
            placeholder={"https://example.com/store-12\nhttps://example.com/store-14"}
            className={`${inputClass} font-mono`} />
          <span className="block text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-1.5">
            Up to 1,000 at a time. Paste with a <span className="font-mono">destination_url,name,group</span> header row to name them too.
          </span>
        </label>
      ) : (
        <div>
          <label className="block">
            <span className="block text-sm font-semibold mb-1.5">CSV file</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-plasma file:text-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-plasma-dark file:cursor-pointer" />
          </label>
          {file && <p className="mt-1.5 font-mono text-xs truncate">{file.name}</p>}
          <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-2">
            Up to 1,000 rows. Header row required; <span className="font-mono">name</span> and <span className="font-mono">group</span> are optional. A new group name makes a new folder.
          </p>
          <pre className="mt-1.5 font-mono text-xs bg-[color:var(--color-cloud)] dark:bg-coal rounded px-3 py-2 overflow-x-auto">{"destination_url,name,group\nhttps://example.com/store-12,Store 12,Windows"}</pre>
        </div>
      )}

      <label className="block mt-4">
        <span className="block text-sm font-semibold mb-1.5">Folder</span>
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputClass}>
          <option value="">No folder</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <span className="block text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-1.5">
          For every link that doesn't name its own.
        </span>
      </label>

      <StylePicker value={referenceToken} onChange={setReferenceToken} inputClass={inputClass} />
    </div>
  )
}

// "Make these look like that one": copy the colors, frame and logo of a code
// you've already styled in the builder. The most recent 50 are offered.
function StylePicker({ value, onChange, inputClass }) {
  const [codes, setCodes] = useState(null)
  useEffect(() => {
    let cancelled = false
    fetchMeCodes({ per: 50, sort: "new" })
      .then((payload) => { if (!cancelled) setCodes(payload.codes) })
      .catch(() => { if (!cancelled) setCodes([]) })
    return () => { cancelled = true }
  }, [])

  return (
    <label className="block mt-4">
      <span className="block text-sm font-semibold mb-1.5">Look</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} disabled={codes === null}>
        <option value="">Plain black and white</option>
        {(codes || []).map((c) => (
          <option key={c.token} value={c.token}>Match {c.display_label}</option>
        ))}
      </select>
      <span className="block text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-1.5">
        Style one code in the builder, then pick it here to give every new code the same colors, frame and logo.
      </span>
    </label>
  )
}

function LineList({ rows, heading }) {
  const byReason = rows.reduce((acc, r) => {
    (acc[r.reason] ||= []).push(r.line)
    return acc
  }, {})
  const reasons = Object.keys(byReason)
  if (reasons.length === 0) return null
  return (
    <div className="mt-3">
      <div className="text-sm font-semibold mb-1">{heading}</div>
      <ul className="space-y-1">
        {reasons.map((reason) => (
          <li key={reason}>
            <details className="text-sm">
              <summary className="cursor-pointer text-[color:var(--color-muted)] dark:text-muted-light">
                <span className="font-mono">{byReason[reason].length.toLocaleString()}</span> {REASONS[reason] || reason}
              </summary>
              <p className="mt-1 ml-4 font-mono text-xs break-all">Line {byReason[reason].join(", ")}</p>
            </details>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Review({ preview }) {
  return (
    <div>
      <p className="text-base">
        <span className="font-mono font-semibold">{preview.creatable.toLocaleString()}</span>
        {` ${preview.creatable === 1 ? "code is" : "codes are"} ready to create.`}
      </p>
      {preview.new_groups.length > 0 && (
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
          New {preview.new_groups.length === 1 ? "folder" : "folders"}: {preview.new_groups.join(", ")}
        </p>
      )}
      <LineList rows={preview.invalid} heading={`${preview.invalid.length.toLocaleString()} ${preview.invalid.length === 1 ? "line" : "lines"} will be left out`} />
      {preview.shapes_dropped && (
        <p className="text-sm mt-3 rounded-lg border border-plasma/40 bg-plasma-pale dark:bg-plasma/15 px-3 py-2">
          That code uses Pro shapes. Your new codes get its colors, frame and logo, with square modules.
        </p>
      )}
      <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-4">
        Every link is checked for safety as it's created, so one that's flagged may still be left out.
      </p>
    </div>
  )
}

function Done({ batch }) {
  if (batch.status === "failed") {
    return (
      <p role="alert" className="text-base text-fail">
        Something went wrong partway through.{" "}
        {batch.created_count > 0
          ? `${noun(batch.created_count)} ${batch.created_count === 1 ? "was made and is" : "were made and are"} in your list.`
          : "Nothing was made."}{" "}
        Try the rest again.
      </p>
    )
  }
  return (
    <div role="status">
      <p className="text-base">
        Created <span className="font-mono font-semibold">{batch.created_count.toLocaleString()}</span>
        {` ${batch.created_count === 1 ? "code" : "codes"}.`}
      </p>
      <LineList rows={batch.skipped} heading={`${batch.skipped_count.toLocaleString()} left out`} />
      {batch.created_count > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {batch.bundle_ready && (
            <a href={codeCreateDownloadUrl(batch.id, "zip")}
              className="bg-plasma text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-plasma-dark transition">
              Download images (ZIP)
            </a>
          )}
          <a href={codeCreateDownloadUrl(batch.id, "csv")}
            className="rounded-lg px-3 py-1.5 text-sm font-medium border border-[color:var(--color-border)] dark:border-storm hover:border-voltage transition">
            Download CSV
          </a>
        </div>
      )}
    </div>
  )
}

function PrimaryButton({ children, ...props }) {
  return (
    <button type="button" {...props}
      className="bg-voltage text-coal rounded-lg px-4 py-2 text-sm font-semibold hover:bg-voltage-dark transition disabled:opacity-50 disabled:cursor-not-allowed">
      {children}
    </button>
  )
}

function GhostButton({ children, ...props }) {
  return (
    <button type="button" {...props}
      className="rounded-lg px-4 py-2 text-sm font-medium border border-[color:var(--color-border)] dark:border-storm hover:border-voltage transition disabled:opacity-50">
      {children}
    </button>
  )
}
